import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  GoogleMap, 
  Marker, 
  Circle, 
  InfoWindow 
} from '@react-google-maps/api';
import { useGoogleMapsLoader } from '../../utils/googleMapsLoader';
import { 
  Notifications as AlertIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Check as ResolveIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Diamond as DiamondIcon,
  AccessTime as TimeIcon,
  PriorityHigh as PriorityHighIcon,
  FilterList as FilterIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { mockAlerts, mockAssets, mockUsers } from '../../mock/mockData';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '400px',
  borderRadius: '8px'
};

// Default center is Johannesburg CBD
const johannesburgCenter = { lat: -26.204103, lng: 28.047304 };

// Johannesburg area locations for reference
const johannesburgLocations = [
  { name: 'Sandton', coordinates: { lat: -26.107567, lng: 28.056702 } },
  { name: 'Rosebank', coordinates: { lat: -26.145738, lng: 28.092537 } },
  { name: 'Hyde Park', coordinates: { lat: -26.116543, lng: 28.034521 } },
  { name: 'Melrose Arch', coordinates: { lat: -26.131901, lng: 28.068121 } },
  { name: 'Fourways', coordinates: { lat: -26.023087, lng: 28.011439 } },
  { name: 'Braamfontein', coordinates: { lat: -26.192366, lng: 28.038582 } },
  { name: 'Johannesburg CBD', coordinates: { lat: -26.204103, lng: 28.047304 } }
];

const options = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  // Add South African specific map features
  mapTypeId: 'roadmap',
  styles: [
    {
      featureType: 'poi.business',
      stylers: [{ visibility: 'on' }] // Show points of interest for businesses
    },
    {
      featureType: 'transit',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'on' }] // Show transit options
    }
  ]
};

// Placeholder for InfoWindow options - will be defined inside component after API loads

// Get marker icon based on alert severity and type
const getAlertMarkerIcon = (severity, type, google) => {
  if (!google || !google.maps) return null;
  
  // Base colors based on severity
  const getColor = (severity) => {
    switch (severity) {
      case 'critical': return '#f44336'; // Red
      case 'warning': return '#ff9800';  // Orange/amber
      case 'info': return '#2196f3';     // Blue
      default: return '#f44336';         // Default to red
    }
  };
  
  // Create a circular marker with the appropriate color
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: getColor(severity),
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#ffffff',
    scale: type === 'PANIC' ? 18 : 15,  // Larger for panic alerts
  };
};

// Get label for alert marker based on type
const getAlertLabel = (type) => {
  // Use first letter of alert type as label
  switch (type) {
    case 'PANIC': return { text: '!', color: '#ffffff', fontSize: '14px', fontWeight: 'bold' };
    case 'tamper_detected': return { text: 'T', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    case 'geofence_exit': return { text: 'G', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    case 'battery_low': return { text: 'B', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    case 'unusual_movement': return { text: 'U', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    case 'speed_alert': return { text: 'S', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    case 'stopped_movement': return { text: 'S', color: '#ffffff', fontSize: '12px', fontWeight: 'bold' };
    default: return { text: '•', color: '#ffffff', fontSize: '14px', fontWeight: 'bold' };
  }
};

// Get asset marker icon
const getAssetMarkerIcon = (status) => {
  return {
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
    fillColor: status === 'normal' ? '#4caf50' : status === 'warning' ? '#ff9800' : '#f44336',
    fillOpacity: 0.7,
    strokeWeight: 1,
    strokeColor: '#ffffff',
    scale: 1.2,
    anchor: { x: 12, y: 22 },
  };
};

// Get alert severity color
const getAlertSeverityColor = (severity) => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
};

// Get alert type label
const getAlertTypeLabel = (type) => {
  switch (type) {
    case 'PANIC':
      return 'Panic';
    case 'geofence_exit':
      return 'Geofence Exit';
    case 'battery_low':
      return 'Battery Low';
    case 'tampering':
      return 'Tampering';
    case 'movement':
      return 'Movement';
    case 'unusual_movement':
      return 'Unusual Movement';
    case 'speed_alert':
      return 'Speed Alert';
    case 'tamper_detected':
      return 'Tamper Detected';
    case 'stopped_movement':
      return 'Stopped Movement';
    default:
      return type;
  }
};

// Get alert severity icon
const getAlertSeverityIcon = (severity) => {
  switch (severity) {
    case 'critical':
      return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'warning':
      return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    case 'info':
      return <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

const AlertMap = ({ assets = [], alerts = mockAlerts, fullScreen = false }) => {
  const [map, setMap] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [mapAlerts, setMapAlerts] = useState([]);
  const [showAlertPanel, setShowAlertPanel] = useState(true);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [alertToResolve, setAlertToResolve] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [infoWindowOptions, setInfoWindowOptions] = useState(null);
  
  const { isLoaded, loadError, showFallback } = useGoogleMapsLoader();

  // Ref for custom InfoWindow styling
  const mapRef = useRef(null);

  // Process alerts with additional data
  useEffect(() => {
    if (alerts && assets) {
      const processedAlerts = alerts.map(alert => {
        const asset = assets.find(a => a.id === alert.assetId) || {};
        const user = mockUsers.find(u => u.id === alert.userId) || {};
        
        return {
          ...alert,
          assetName: asset.name || 'Unknown Asset',
          assetType: asset.type || 'Unknown',
          userName: user.name || 'Unknown User',
          userEmail: user.email || 'Unknown',
          // Use asset location if alert doesn't have one
          location: alert.location || asset.location
        };
      });
      
      setMapAlerts(processedAlerts);
    }
  }, [alerts, assets]);

  // Map load callback
  const onLoad = useCallback(function callback(map) {
    setMap(map);
    mapRef.current = map;
    
    // Add custom styling to hide the default InfoWindow close button and fix centering
    const style = document.createElement('style');
    style.textContent = `
      /* Hide default close button */
      .gm-style-iw-c button.gm-ui-hover-effect,
      .gm-style-iw button.gm-ui-hover-effect,
      button.gm-ui-hover-effect {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      
      /* Fix padding and centering */
      .gm-style-iw-c {
        padding: 0 !important;
        max-width: 400px !important;
      }
      
      /* Fix content overflow */
      .gm-style-iw-d {
        overflow: hidden !important;
        max-width: 400px !important;
      }
      
      /* Remove any remnants at the bottom */
      .gm-style-iw-tc, 
      .gm-style-iw-tc::after,
      .gm-style-iw > button,
      .gm-style-iw + div {
        display: none !important;
      }
      
      /* Fix alignment of the InfoWindow */
      .gm-style-iw.gm-style-iw-c {
        transform: translate(-50%, -50%) !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  // Initialize InfoWindow options after Google Maps API is loaded
  useEffect(() => {
    if (isLoaded && window.google && window.google.maps) {
      try {
        setInfoWindowOptions({
          pixelOffset: new window.google.maps.Size(0, -5),
          disableAutoPan: false
        });
      } catch (error) {
        console.error('Error initializing InfoWindow options:', error);
        setInfoWindowOptions({});
      }
    }
  }, [isLoaded]);

  // Calculate bounds to fit all markers
  useEffect(() => {
    if (isLoaded && map && mapAlerts.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add alert locations to bounds
      mapAlerts.forEach(alert => {
        if (alert.location) {
          bounds.extend({
            lat: alert.location.lat,
            lng: alert.location.lng
          });
        }
      });
      
      // Add asset locations to bounds
      assets.forEach(asset => {
        if (asset.location) {
          bounds.extend({
            lat: asset.location.lat,
            lng: asset.location.lng
          });
        }
      });
      
      // Add Johannesburg locations to ensure map is centered on the region
      const johannesburgLocations = [
        { coordinates: { lat: -26.204103, lng: 28.047304 } },
        { coordinates: { lat: -26.204103, lng: 28.047304 } },
        { coordinates: { lat: -26.204103, lng: 28.047304 } },
      ];
      johannesburgLocations.forEach(location => {
        bounds.extend({
          lat: location.coordinates.lat,
          lng: location.coordinates.lng
        });
      });
      
      // Only fit bounds if we have locations
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
        
        // Adjust zoom to focus more on Johannesburg area
        const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > 12) map.setZoom(12); // Don't zoom in too far
        });
      }
    } else if (map) {
      // If no alerts or assets, center on Johannesburg (only if map is initialized)
      map.setCenter(johannesburgCenter);
      map.setZoom(11);
    }
    setBounds(bounds);
  }, [mapAlerts, map, isLoaded]);

  const handleResolveAlert = (alert) => {
    setAlertToResolve(alert);
    setResolveDialogOpen(true);
  };

  const confirmResolveAlert = () => {
    if (!alertToResolve) return;
    
    // Update the alert status in our local state
    const updatedAlerts = mapAlerts.map(alert => 
      alert.id === alertToResolve.id 
        ? { 
            ...alert, 
            status: 'resolved', 
            resolvedAt: new Date().toISOString(),
            notes: resolveNote || alert.notes
          } 
        : alert
    );
    
    setMapAlerts(updatedAlerts);
    setResolveDialogOpen(false);
    setResolveNote('');
    setAlertToResolve(null);
    
    // If the resolved alert was selected, update it or clear selection
    if (selectedAlert && selectedAlert.id === alertToResolve.id) {
      const updatedAlert = updatedAlerts.find(a => a.id === alertToResolve.id);
      if (updatedAlert) {
        setSelectedAlert(updatedAlert);
      } else {
        setSelectedAlert(null);
      }
    }
  };

  const handleDeleteAlert = (alert) => {
    setAlertToDelete(alert);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAlert = () => {
    if (!alertToDelete) return;
    
    // Remove the alert from our local state
    const updatedAlerts = mapAlerts.filter(alert => alert.id !== alertToDelete.id);
    setMapAlerts(updatedAlerts);
    setDeleteDialogOpen(false);
    
    // If the deleted alert was selected, clear selection
    if (selectedAlert && selectedAlert.id === alertToDelete.id) {
      setSelectedAlert(null);
    }
    
    setAlertToDelete(null);
  };

  if (loadError || showFallback) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          bgcolor: 'grey.100',
          borderRadius: '8px',
          p: 3
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Map Unavailable
        </Typography>
        <Typography color="text.secondary" align="center" sx={{ mb: 2 }}>
          The map could not be loaded. Alert management is still available below.
        </Typography>
        
        {/* Show alerts list when map is unavailable */}
        <Paper 
          elevation={2} 
          sx={{ 
            width: '100%', 
            maxWidth: 600,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2
          }}
        >
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Active Alerts
            </Typography>
            <List dense>
              {alerts.filter(alert => alert.status === 'active').map((alert) => (
                <React.Fragment key={alert.id}>
                  <ListItem 
                    button
                    onClick={() => setSelectedAlert(alert)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getAlertSeverityColor(alert.severity) }}>
                        {getAlertSeverityIcon(alert.severity)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {getAlertTypeLabel(alert.type)}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={alert.severity} 
                            color={alert.severity === 'critical' ? 'error' : 'warning'}
                            sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, py: 0.1 } }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <LocationIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.875rem' }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {alert.assetName}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <TimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                            {new Date(alert.timestamp).toLocaleTimeString()}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size="small" 
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResolveAlert(alert);
                        }}
                      >
                        <ResolveIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
              
              {alerts.filter(alert => alert.status === 'active').length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No active alerts"
                    secondary="All systems operating normally"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Default center (will be overridden by bounds)
  const defaultCenter = { lat: -26.204103, lng: 28.047304 }; // Johannesburg, South Africa

  // Count active alerts
  const activeAlertsCount = mapAlerts.filter(alert => alert.status === 'active').length;

  return (
    <Box sx={{ 
      height: fullScreen ? '650px' : '100%', 
      width: '100%', 
      display: 'flex',
      position: 'relative'
    }}>
      {/* Map Container */}
      <Box sx={{ 
        flexGrow: 1, 
        height: '100%', 
        width: showAlertPanel ? 'calc(100% - 350px)' : '100%',
        transition: 'width 0.3s ease-in-out'
      }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{ lat: -26.204103, lng: 28.047304 }} // Default center on Johannesburg
          zoom={11} // Good zoom level for city view
          options={options}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {/* Asset Markers */}
          {assets.map((asset) => (
            <Marker 
              key={`asset-${asset.id}`}
              position={{
                lat: asset.location.lat,
                lng: asset.location.lng
              }}
              onClick={() => {
                setSelectedAsset(asset);
                setSelectedAlert(null);
              }}
              icon={getAssetMarkerIcon(asset.status)}
              zIndex={1}
            />
          ))}
          
          {/* Alert Markers */}
          {mapAlerts.filter(alert => alert.status === 'active' && alert.location).map((alert) => (
            <Marker 
              key={`alert-${alert.id}`}
              position={{
                lat: alert.location.lat,
                lng: alert.location.lng
              }}
              onClick={() => {
                setSelectedAlert(alert);
                setSelectedAsset(null);
              }}
              icon={getAlertMarkerIcon(alert.severity, alert.type, window.google)}
              label={getAlertLabel(alert.type)}
              zIndex={2}
              animation={window.google && window.google.maps ? window.google.maps.Animation.DROP : null}
            />
          ))}
          
          {/* Info window for selected alert */}
          {selectedAlert && selectedAlert.location && isLoaded && infoWindowOptions && (
            <InfoWindow
              position={{
                lat: selectedAlert.location.lat,
                lng: selectedAlert.location.lng
              }}
              onCloseClick={() => setSelectedAlert(null)}
              options={infoWindowOptions}
            >
              <Box sx={{ p: 2, width: '100%', maxWidth: '400px', position: 'relative', bgcolor: 'background.paper', borderRadius: 1 }}>
                {/* Close button at top right */}
                <IconButton 
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={() => setSelectedAlert(null)}
                >
                  <CloseIcon />
                </IconButton>
                
                {/* Alert type with colored circle */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: selectedAlert.severity === 'critical' ? '#f44336' : '#ff9800',
                      mr: 2
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
                    {getAlertTypeLabel(selectedAlert.type)}
                  </Typography>
                </Box>
                
                {/* Alert message */}
                <Typography variant="body1" sx={{ mb: 3, color: '#333', fontSize: '1.1rem', fontWeight: 400 }}>
                  {selectedAlert.message}
                </Typography>
                
                {/* Asset info with icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box component="span" sx={{ color: '#1976d2', mr: 1, display: 'flex' }}>
                    <DiamondIcon />
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAlert.assetName}</Typography>
                </Box>
                
                {/* User info with icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box component="span" sx={{ color: '#666', mr: 1, display: 'flex' }}>
                    <PersonIcon />
                  </Box>
                  <Typography variant="body1">{selectedAlert.userName}</Typography>
                </Box>
                
                {/* Timestamp */}
                <Typography variant="body1" color="#666" display="block" sx={{ mb: 3 }}>
                  {new Date(selectedAlert.timestamp).toLocaleString()}
                </Typography>
                
                {/* Action buttons - single container with two buttons */}
                <Box sx={{ 
                  mt: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      sx={{ 
                        flex: 1, 
                        py: 1.5,
                        borderRadius: 0,
                        color: '#4caf50',
                        '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.08)' }
                      }}
                      variant="text" 
                      startIcon={<CheckIcon sx={{ color: '#4caf50' }} />}
                      onClick={() => handleResolveAlert(selectedAlert)}
                    >
                      Resolve
                    </Button>
                    
                    <Button 
                      sx={{ 
                        flex: 1, 
                        py: 1.5,
                        borderRadius: 0,
                        color: '#f44336',
                        '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' }
                      }}
                      variant="text" 
                      startIcon={<DeleteIcon sx={{ color: '#f44336' }} />}
                      onClick={() => handleDeleteAlert(selectedAlert)}
                    >
                      Delete
                    </Button>
                  </Box>
                  
                  {/* Overlay to prevent any remnant buttons from showing */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '10px',
                    bgcolor: 'background.paper',
                    zIndex: 10
                  }} />
                </Box>
              </Box>
            </InfoWindow>
          )}
          
          {/* Info window for selected asset */}
          {selectedAsset && isLoaded && infoWindowOptions && (
            <InfoWindow
              position={{
                lat: selectedAsset.location.lat,
                lng: selectedAsset.location.lng
              }}
              onCloseClick={() => setSelectedAsset(null)}
              options={infoWindowOptions}
            >
              <Box sx={{ p: 1, maxWidth: '250px' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedAsset.name}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{selectedAsset.description}</Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    size="small" 
                    label={selectedAsset.status.toUpperCase()} 
                    color={
                      selectedAsset.status === 'normal' 
                        ? 'success' 
                        : selectedAsset.status === 'warning' 
                          ? 'warning' 
                          : 'error'
                    }
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Type:</Typography>
                  <Typography variant="body2">{selectedAsset.type}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Value:</Typography>
                  <Typography variant="body2">${selectedAsset.value.toLocaleString()}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Battery:</Typography>
                  <Chip 
                    size="small" 
                    label={`${selectedAsset.batteryLevel}%`} 
                    color={
                      selectedAsset.batteryLevel > 60 
                        ? 'success' 
                        : selectedAsset.batteryLevel > 20 
                          ? 'warning' 
                          : 'error'
                    }
                    variant="outlined"
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Last Updated: {new Date(selectedAsset.lastUpdated).toLocaleString()}
                </Typography>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
        
        {/* Alert Panel Toggle Button */}
        <Box sx={{ 
          position: 'absolute', 
          top: 10, 
          right: showAlertPanel ? 360 : 10, 
          zIndex: 10,
          transition: 'right 0.3s ease-in-out'
        }}>
          <Tooltip title={showAlertPanel ? "Hide Alert Panel" : "Show Alert Panel"}>
            <IconButton 
              color="primary" 
              sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
              onClick={() => setShowAlertPanel(!showAlertPanel)}
            >
              <Badge badgeContent={activeAlertsCount} color="error" invisible={activeAlertsCount === 0}>
                {showAlertPanel ? <CloseIcon /> : <AlertIcon />}
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Alert Panel */}
      {showAlertPanel && (
        <Paper 
          sx={{ 
            width: 350, 
            height: '100%', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 3
          }}
        >
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AlertIcon sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6">Active Alerts</Typography>
            </Box>
            <Chip 
              label={`${activeAlertsCount} active`} 
              color="error" 
              size="small"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <List sx={{ width: '100%' }}>
              {mapAlerts
                .filter(alert => alert.status === 'active')
                .sort((a, b) => {
                  // Sort by severity first (critical, warning, info)
                  const severityOrder = { critical: 0, warning: 1, info: 2 };
                  const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
                  
                  // If same severity, sort by timestamp (newest first)
                  if (severityDiff === 0) {
                    return new Date(b.timestamp) - new Date(a.timestamp);
                  }
                  
                  return severityDiff;
                })
                .map((alert) => (
                  <React.Fragment key={alert.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      button
                      selected={selectedAlert && selectedAlert.id === alert.id}
                      onClick={() => {
                        setSelectedAlert(alert);
                        setSelectedAsset(null);
                        
                        // Center map on the alert
                        if (map && alert.location) {
                          map.panTo({ lat: alert.location.lat, lng: alert.location.lng });
                          map.setZoom(15);
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: alert.severity === 'critical' 
                              ? 'error.main' 
                              : alert.severity === 'warning' 
                                ? 'warning.main' 
                                : 'info.main' 
                          }}
                        >
                          {alert.severity === 'critical' 
                            ? <PriorityHighIcon /> 
                            : <WarningIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" noWrap sx={{ maxWidth: 150 }}>
                              {getAlertTypeLabel(alert.type)}
                            </Typography>
                            <Chip
                              size="small"
                              label={alert.severity}
                              color={getAlertSeverityColor(alert.severity)}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.primary" noWrap>
                              {alert.message}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <DiamondIcon fontSize="inherit" sx={{ mr: 0.5, color: 'primary.main' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {alert.assetName}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                              <TimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                              {new Date(alert.timestamp).toLocaleTimeString()}
                            </Typography>
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          size="small" 
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolveAlert(alert);
                          }}
                        >
                          <ResolveIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
                
              {activeAlertsCount === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No active alerts"
                    secondary="All systems operating normally"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Paper>
      )}
      
      {/* Resolve Alert Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)}>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resolve this alert?
            {alertToResolve && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">{getAlertTypeLabel(alertToResolve.type)}</Typography>
                <Typography variant="body2">{alertToResolve.message}</Typography>
                <Typography variant="body2">Asset: {alertToResolve.assetName}</Typography>
              </Box>
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="resolution-note"
            label="Resolution Note"
            type="text"
            fullWidth
            variant="outlined"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmResolveAlert} variant="contained" color="success">
            Resolve Alert
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Alert Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Alert</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this alert? This action cannot be undone.
            {alertToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">{getAlertTypeLabel(alertToDelete.type)}</Typography>
                <Typography variant="body2">{alertToDelete.message}</Typography>
                <Typography variant="body2">Asset: {alertToDelete.assetName}</Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteAlert} variant="contained" color="error">
            Delete Alert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertMap;
